import SignType from "../signType";
import { rowsOf } from "./composer";
import { signedTypeOptionsFor } from "./signedTypeOptions";

/* Codes match server/domain/products/productProblems.js so a server rejection and a client
 * check describe a problem the same way; the IMAGE_ ones are the client's own. */
export const PROBLEM = Object.freeze({
  DUPLICATE_NAME: "DUPLICATE_NAME",
  IMAGE_NOT_READY: "IMAGE_NOT_READY",
  IMAGE_TOO_SMALL: "IMAGE_TOO_SMALL",
  MISSING_NAME: "MISSING_NAME",
  NAME_TOO_LONG: "NAME_TOO_LONG",
  NO_CARDS: "NO_CARDS",
  NO_IMAGE: "NO_IMAGE",
  NO_SIGNED_TYPE: "NO_SIGNED_TYPE",
  NO_SIGNERS: "NO_SIGNERS",
  SIGNED_TYPE_NOT_ALLOWED: "SIGNED_TYPE_NOT_ALLOWED",
  SIGNED_TYPE_WRONG_TYPE: "SIGNED_TYPE_WRONG_TYPE",
  TOO_MANY_IMAGES: "TOO_MANY_IMAGES",
  TOO_MANY_ROWS: "TOO_MANY_ROWS",
  ZERO_PRICE: "ZERO_PRICE",
});

const MAX_ROWS = 50;
const MAX_IMAGES = 10;
const MAX_NAME_LENGTH = 100;

const SHOUTOUT_SIGNED_TYPE = Object.freeze({
  video: "Shoutout",
  audio: "Audio Shoutout",
});

const SHOUTOUT_SIGNED_TYPES = new Set(Object.values(SHOUTOUT_SIGNED_TYPE));

/* The closest browser match to product.name's utf8mb4_unicode_ci collation. */
const sameName = (a, b) =>
  a.trim().localeCompare(b.trim(), undefined, { sensitivity: "base" }) === 0;

const fitsProductType = (productType, signedType) =>
  productType?.shoutoutKind
    ? signedType === SHOUTOUT_SIGNED_TYPE[productType.shoutoutKind]
    : !SHOUTOUT_SIGNED_TYPES.has(signedType);

const when = (condition, problem) => (condition ? [problem] : []);

const mayOffer = (user, signedType) =>
  signedTypeOptionsFor({ signedTypes: [signedType], user }).length > 0;

const batchProblems = (state) => [
  ...when(state.cards.length === 0, { code: PROBLEM.NO_CARDS }),
  ...when(state.signedTypes.length === 0, { code: PROBLEM.NO_SIGNED_TYPE }),
  ...when(state.signers.length === 0, { code: PROBLEM.NO_SIGNERS }),
  ...when(rowsOf(state).length > MAX_ROWS, { code: PROBLEM.TOO_MANY_ROWS }),
];

const priceOf = (state, signedType) =>
  state.signers.reduce(
    (sum, signer) => sum + (signer.splits[signedType] ?? 0),
    0,
  );

const signedTypeProblems = ({ state, productType, user }) =>
  state.signedTypes.flatMap((signedType) => [
    ...when(!mayOffer(user, signedType), {
      code: PROBLEM.SIGNED_TYPE_NOT_ALLOWED,
      signedType,
    }),
    ...when(!fitsProductType(productType, signedType), {
      code: PROBLEM.SIGNED_TYPE_WRONG_TYPE,
      signedType,
    }),
    ...when(state.signers.length > 0 && priceOf(state, signedType) <= 0, {
      code: PROBLEM.ZERO_PRICE,
      signedType,
    }),
  ]);

const imageProblem = ({ cardId, images }) =>
  images.length === 0
    ? [{ code: PROBLEM.NO_IMAGE, cardId }]
    : images.length > MAX_IMAGES
    ? [{ code: PROBLEM.TOO_MANY_IMAGES, cardId }]
    : images.some((image) => image.status === "too-small")
    ? [{ code: PROBLEM.IMAGE_TOO_SMALL, cardId }]
    : images.some((image) => image.status !== "uploaded")
    ? [{ code: PROBLEM.IMAGE_NOT_READY, cardId }]
    : [];

const nameProblem = ({ cardId, name }) =>
  !name.trim()
    ? [{ code: PROBLEM.MISSING_NAME, cardId }]
    : name.length > MAX_NAME_LENGTH
    ? [{ code: PROBLEM.NAME_TOO_LONG, cardId }]
    : [];

const cardProblems = (card) => [...nameProblem(card), ...imageProblem(card)];

const duplicateProblems = ({ state, existingProducts }) => {
  const named = state.cards.filter((card) => card.name.trim());

  return named.flatMap((card) =>
    state.signedTypes.flatMap((signedType) => {
      const existing = existingProducts.find(
        (product) =>
          product.signedType === signedType &&
          sameName(product.name, card.name),
      );
      const sharedInBatch = named.some(
        (other) =>
          other.cardId !== card.cardId && sameName(other.name, card.name),
      );

      return [
        ...when(Boolean(existing), {
          code: PROBLEM.DUPLICATE_NAME,
          cardId: card.cardId,
          signedType,
          productId: existing?.productId,
        }),
        ...when(sharedInBatch, {
          code: PROBLEM.DUPLICATE_NAME,
          cardId: card.cardId,
          signedType,
        }),
      ];
    }),
  );
};

export const offeredSignedTypes = ({ signedTypes, productType, user }) =>
  signedTypeOptionsFor({ signedTypes, user }).filter((signedType) =>
    fitsProductType(productType, signedType),
  );

export const composerProblems = ({
  state,
  productTypes,
  existingProducts,
  user,
}) => {
  const productType = productTypes.find(
    (type) => type.productTypeId === state.productTypeId,
  );

  return [
    ...batchProblems(state),
    ...signedTypeProblems({ state, productType, user }),
    ...state.cards.flatMap(cardProblems),
    ...duplicateProblems({ state, existingProducts }),
  ];
};

const label = (signedType) => SignType.toLabel(signedType);

const MESSAGES = Object.freeze({
  [PROBLEM.DUPLICATE_NAME]: ({ signedType }) =>
    `You already have a ${label(signedType)} product with this name`,
  [PROBLEM.IMAGE_NOT_READY]: () =>
    "Wait for this product's images to finish uploading",
  [PROBLEM.IMAGE_TOO_SMALL]: () =>
    "An image is too small for this product type. Edit or replace it",
  [PROBLEM.MISSING_NAME]: () => "Give this product a name",
  [PROBLEM.NAME_TOO_LONG]: () => "Keep the name to 100 characters",
  [PROBLEM.NO_CARDS]: () => "Add a product",
  [PROBLEM.NO_IMAGE]: () => "Add at least one image",
  [PROBLEM.NO_SIGNED_TYPE]: () => "Choose at least one sign type",
  [PROBLEM.NO_SIGNERS]: () => "Add at least one signer",
  [PROBLEM.SIGNED_TYPE_NOT_ALLOWED]: ({ signedType }) =>
    `Only admins can offer ${label(signedType)}`,
  [PROBLEM.SIGNED_TYPE_WRONG_TYPE]: ({ signedType }) =>
    `${label(signedType)} can't be offered on this product type`,
  [PROBLEM.TOO_MANY_IMAGES]: () => "Use 10 images or fewer",
  [PROBLEM.TOO_MANY_ROWS]: () => "A batch can create at most 50 products",
  [PROBLEM.ZERO_PRICE]: ({ signedType }) =>
    `${label(signedType)} needs a price above $0`,
  SERVICE_NOT_OFFERED: () =>
    "A selected service isn't offered by any signer on this product",
});

export const messageFor = (problem) =>
  MESSAGES[problem.code]?.(problem) ?? "This product could not be saved";

const PAGE_OF = Object.freeze({
  product: (problem) => Boolean(problem.cardId),
  pricing: (problem) => !problem.cardId,
  review: () => true,
});

export const problemsOnPage = (problems, page) =>
  problems.filter(PAGE_OF[page] ?? (() => false));
